import "devextreme/dist/css/dx.fluent.blue.light.compact.css";
import { useEffect, useState } from "react";
import DataGrid, { Column, Pager, Paging, SearchPanel } from "devextreme-react/data-grid";
import { useDevTableRows, useDevTables } from "../../hooks/useApi";
import { ErrorState } from "../common/ErrorState";

/** Database browser: table list on the left, DevExtreme grid on the right. */
export function DatabaseTab() {
  const [table, setTable] = useState<string | null>(null);
  const tablesQuery = useDevTables(true);
  const rowsQuery = useDevTableRows(table);

  // Dismiss the DevExtreme trial license notice once the grid renders
  // (no-op when the calendar already dismissed it).
  useEffect(() => {
    const parent = document.querySelector("dx-license");
    if (parent) {
      const secondDiv = parent.querySelectorAll("div")[1];
      if (secondDiv) secondDiv.click();
    }
  }, [rowsQuery.data]);

  const data = rowsQuery.data;
  return (
    <div className="flex h-full">
      <aside className="w-60 shrink-0 overflow-y-auto border-r border-stroke">
        {tablesQuery.isLoading && (
          <p className="px-3 py-2 text-[12px] text-ink-muted">Loading tables…</p>
        )}
        {tablesQuery.error && (
          <p className="px-3 py-2 text-[12px] text-red-700">
            {(tablesQuery.error as Error).message}
          </p>
        )}
        {(tablesQuery.data ?? []).map((t) => (
          <button
            key={t.name}
            onClick={() => setTable(t.name)}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-black/5 ${
              table === t.name ? "bg-accent/10 font-semibold text-accent" : "text-ink"
            }`}
          >
            <span className="min-w-0 flex-1 truncate">{t.name}</span>
            <span className="shrink-0 text-[11px] tabular-nums text-ink-muted">
              ~{t.rowCount.toLocaleString()}
            </span>
          </button>
        ))}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col p-2">
        {table === null ? (
          <p className="p-4 text-[13px] text-ink-muted">Select a table to browse its data.</p>
        ) : rowsQuery.isLoading ? (
          <p className="p-4 text-[13px] text-ink-muted">Loading {table}…</p>
        ) : rowsQuery.error ? (
          <ErrorState error={rowsQuery.error as Error} />
        ) : data ? (
          <>
            <p className="px-1 pb-1.5 text-[12px] text-ink-muted">
              {table} — showing {data.rows.length.toLocaleString()} of{" "}
              {data.total.toLocaleString()} rows
            </p>
            <div className="min-h-0 flex-1">
              <DataGrid
                key={table}
                dataSource={data.rows}
                height="100%"
                showBorders
                columnAutoWidth
                allowColumnResizing
                wordWrapEnabled={false}
              >
                <SearchPanel visible width={220} />
                <Paging defaultPageSize={25} />
                <Pager visible showPageSizeSelector allowedPageSizes={[25, 50, 100]} showInfo />
                {data.columns.map((c) => (
                  <Column key={c} dataField={c} />
                ))}
              </DataGrid>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
